<template>
  <v-dialog :value="value" max-width="860" persistent scrollable @input="$emit('input', $event)">
    <v-form ref="form" @submit.prevent="submit">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t('onboarding.commissioning.form.title') }}
          <v-spacer />
          <v-btn icon :aria-label="$t('close')" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <!-- 1. Pour qui : depuis l'organisation, elle-même, un de ses membres, ou une personne pas encore membre (invitée). -->
          <div class="equipment-order__step"><span class="equipment-order__number">1</span>{{ $t('onboarding.commissioning.form.step-who') }}</div>
          <template v-if="holder === 'organization'">
            <v-radio-group v-model="target" dense hide-details class="mt-0 mb-3" @change="whoChanged">
              <v-radio value="organization" :label="$t('onboarding.commissioning.form.who-organization')" :disabled="!organizationCanOrder" />
              <v-radio value="member" :label="$t('onboarding.commissioning.form.who-member')" />
              <v-radio value="invitee" :label="$t('onboarding.commissioning.form.who-invitee')" />
            </v-radio-group>
            <member-select
                v-if="target === 'member'"
                v-model="pickedMemberId"
                :organization-id="organizationId"
                :label="$t('onboarding.commissioning.form.member')"
                :rules="[required]"
                instance-id="equipment-order-member"
                class="mb-4"
            />
            <template v-if="invitee">
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-text-field v-model="person.firstname" :label="$t('onboarding.commissioning.form.firstname')" :rules="[required]" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="person.lastname" :label="$t('onboarding.commissioning.form.lastname')" :rules="[required]" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="person.email" type="email" :label="$t('onboarding.commissioning.form.invitee-email')" :rules="[required]" outlined dense />
                </v-col>
              </v-row>
              <!-- Sa politique : celle de son invitation, et celle qui dit quel profil s'applique chez elle -->
              <v-select
                  :value="chosenPolicyId" :items="policies" item-text="name" item-value="id"
                  :label="$t('onboarding.commissioning.form.invitee-policy')" :hint="$t('onboarding.commissioning.form.invitee-policy-hint')" persistent-hint
                  outlined dense class="mb-3"
                  @change="choosePolicy"
              >
                <template #item="{ item }">
                  {{ item.name }}<v-chip v-if="item.default" x-small outlined color="primary" class="ml-2">{{ $t('onboarding.charging-policy.list.default') }}</v-chip>
                </template>
              </v-select>
              <v-alert type="info" dense text class="mb-4">{{ $t('onboarding.commissioning.form.invitee-explained') }}</v-alert>
            </template>
          </template>
          <v-alert v-else border="left" colored-border color="primary" elevation="1" dense class="mb-4">
            {{ forWhom }}
          </v-alert>

          <!-- 2. Quel profil : par type d'équipement, ce qui se commande pour cette personne ou cette organisation ; le reste, dit. -->
          <div class="equipment-order__step"><span class="equipment-order__number">2</span>{{ $t('onboarding.commissioning.form.step-profile') }}</div>
          <v-progress-linear v-if="loadingPolicy" indeterminate class="mb-3" />
          <template v-else-if="whoKnown">
            <!-- Les familles sur deux colonnes, leurs profils l'un sous l'autre : pas de demi-ligne vide à droite. -->
            <v-row dense>
              <v-col v-for="group in profileGroups" :key="group.kind" cols="12" md="6" class="mb-2">
                <div class="text-caption font-weight-bold text--secondary mb-1">
                  <v-icon x-small class="mr-1">{{ kindIcon(group.kind) }}</v-icon>{{ kindLabel(group.kind) }}
                </div>
                <!-- Une borne, un compteur : son type d'abord, il dit le profil (et donc l'abonnement) -->
                <!-- Un seul possible : il l'est d'office, rien à choisir -->
                <div v-if="group.deviceTypes.length === 1" class="text-caption text--secondary mb-1">
                  {{ $t(`onboarding.commissioning.form.device-type.${group.kind}`) }} : <b>{{ deviceTypeLabel(group.deviceTypes[0]) }}</b>
                </div>
                <div v-else-if="group.deviceTypes.length" class="mb-1">
                  <div class="text-body-2 font-weight-medium" :class="{ 'error--text': deviceTypeMissing && kind === group.kind }">
                    {{ $t(`onboarding.commissioning.form.device-type-question.${group.kind}`) }}
                  </div>
                  <v-radio-group :value="deviceTypeByKind[group.kind] || null" row dense hide-details class="mt-1 pt-0" @change="chooseDeviceType(group.kind, $event)">
                    <v-radio v-for="deviceType in group.deviceTypes" :key="deviceType" :value="deviceType" :label="deviceTypeLabel(deviceType)" />
                  </v-radio-group>
                </div>
                <v-row dense>
                  <v-col v-for="option in group.choices" :key="option.key" cols="12">
                    <v-card
                        outlined
                        :class="{ 'equipment-order__choice--on': isChosen(option) }"
                        class="pa-3 fill-height"
                        @click="choose(option)"
                    >
                      <div class="d-flex align-center flex-wrap" style="gap: 6px">
                        <span class="font-weight-medium">{{ option.title }}</span>
                        <v-chip v-if="option.usage" x-small outlined color="primary">{{ $t(`onboarding.equipment-profile.usage.${option.usage}`) }}</v-chip>
                      </div>
                      <div class="text-caption text--secondary">{{ option.detail }}</div>
                    </v-card>
                  </v-col>
                  <v-col v-if="group.reason" cols="12">
                    <div class="equipment-order__unavailable text-caption">
                      {{ $t(`onboarding.commissioning.form.unavailable.${group.reason}`) }}
                      <router-link v-if="group.reason === 'no-site-profile' && canManageProfiles" :to="{ name: 'organizations.equipment-profiles', params: { id: organizationId } }">{{ $t('onboarding.commissioning.form.manage-profiles') }}</router-link>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <p v-if="!profileGroups.length" class="text-caption text--secondary">{{ $t('onboarding.commissioning.form.no-kind') }}</p>
          </template>
          <p v-else class="text-caption text--secondary">{{ $t('onboarding.commissioning.form.who-first') }}</p>
          <v-alert v-if="deviceTypeMissing" type="warning" dense text class="mb-3">{{ $t('onboarding.commissioning.form.device-type-required') }}</v-alert>

          <template v-if="kind">
            <!-- 3. Où : pour une personne, chez elle (un lieu où elle a déjà un équipement, son adresse, une nouvelle) ; pour l'organisation, un de ses emplacements. -->
            <template v-if="installable">
              <div class="equipment-order__step mt-2"><span class="equipment-order__number">3</span>{{ $t('onboarding.commissioning.form.step-where') }}</div>
              <v-radio-group v-if="placeOptions.length > 1" v-model="locationChoice" dense hide-details class="mt-0 mb-3">
                <v-radio v-for="option in placeOptions" :key="option.value" :value="option.value" :disabled="option.disabled">
                  <template #label>
                    <div>
                      <div>{{ option.label }}</div>
                      <div v-if="option.detail" class="text-caption" :class="option.disabled ? 'orange--text text--darken-3' : 'text--secondary'">{{ option.detail }}</div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
              <v-select
                  v-if="locationChoice === 'existing'"
                  v-model="locationId" :items="locations" item-text="name" item-value="id"
                  :label="$t('onboarding.commissioning.location')"
                  :rules="[v => !!v || $t('onboarding.commissioning.form.location-required')]"
                  prepend-inner-icon="mdi-map-marker-outline"
                  outlined dense
              />
              <new-location-picker
                  v-else-if="locationChoice === 'new'"
                  v-model="newPlace"
                  :organization-id="organizationId"
                  :label="$t('onboarding.commissioning.form.address')"
                  :hint="$t('onboarding.commissioning.form.address-hint')"
                  required
              />

              <template v-if="who === 'member' && !invitee">
                <v-text-field
                    v-model="person.email"
                    type="email"
                    :label="$t('onboarding.commissioning.form.email')"
                    :hint="$t('onboarding.commissioning.form.email-hint')" persistent-hint
                    outlined dense class="mb-4"
                />
              </template>

              <div class="equipment-order__step equipment-order__step--sub mt-2">{{ $t(`onboarding.commissioning.form.step-equipment-${kind}`) }}</div>
              <v-radio-group v-model="equipmentMode" dense hide-details class="mt-0 mb-3">
                <v-radio value="unknown" :label="$t(`onboarding.commissioning.form.equipment-unknown-${kind}`)" />
                <v-radio value="existing" :label="$t(`onboarding.commissioning.form.equipment-existing-${kind}`)" />
              </v-radio-group>

              <template v-if="equipmentMode === 'existing'">
                <v-autocomplete
                    v-model="prepared"
                    :items="equipments" :loading="searchingEquipments" :search-input.sync="equipmentSearch"
                    item-text="label" return-object no-filter hide-no-data clearable
                    :label="$t(`onboarding.commissioning.form.equipment-search-${kind}`)"
                    :rules="[v => !!v || $t('onboarding.commissioning.form.equipment-required')]"
                    prepend-inner-icon="mdi-magnify"
                    outlined dense
                >
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.details }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <v-alert v-if="prepared" border="left" colored-border color="primary" elevation="1" dense class="mb-4">
                  <div class="font-weight-medium">{{ prepared.label }}</div>
                  <div class="text-caption">{{ prepared.details }}</div>
                  <div class="text-caption mt-1">{{ $t('onboarding.commissioning.form.equipment-existing-hint') }}</div>
                </v-alert>
              </template>

              <template v-else>
                <p class="text-caption text--secondary mb-2">{{ $t('onboarding.commissioning.form.expected-hint') }}</p>
                <v-row dense class="mb-2">
                  <v-col v-if="kind === 'charging-point'" cols="12" md="4">
                    <v-select v-model="currentType" :items="currentTypes" :label="$t('onboarding.commissioning.form.current')" outlined dense hide-details />
                  </v-col>
                  <!-- Une borne : ses constructeurs et ses gammes (ou types) admis, choisis dans le classement. -->
                  <template v-if="kind === 'charging-point'">
                    <v-col cols="12" md="4">
                      <v-combobox v-model="restrictions.vendors" :items="manufacturerNames" multiple small-chips :label="$t('onboarding.commissioning.form.vendors')" outlined dense hide-details @focus="loadClassification" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete v-model="restrictions.models" :items="modelChoices" item-text="text" item-value="id" multiple small-chips :label="$t('onboarding.commissioning.form.models')" outlined dense hide-details @focus="loadClassification" />
                    </v-col>
                  </template>
                  <template v-else>
                    <v-col cols="12" md="4">
                      <v-combobox v-model="restrictions.vendors" multiple small-chips :label="$t('onboarding.commissioning.form.vendors')" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-combobox v-model="restrictions.models" multiple small-chips :label="$t('onboarding.commissioning.form.models')" outlined dense hide-details />
                    </v-col>
                  </template>
                </v-row>
              </template>

              <div class="equipment-order__step equipment-order__step--sub mt-2">{{ $t('onboarding.commissioning.form.step-when') }}</div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field v-model="desiredDate" type="date" :label="$t('onboarding.commissioning.desired-date')"
                                :hint="$t('onboarding.commissioning.form.desired-hint')" persistent-hint outlined dense />
                </v-col>
                <v-col cols="12" md="6">
                  <!-- Chez une personne, la pose attend d'abord sa confirmation : le rendez-vous se fixe ensuite. -->
                  <v-tooltip bottom :disabled="!confirmationFirst">
                    <template #activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <v-text-field v-model="scheduledAt" type="datetime-local" :label="$t('onboarding.commissioning.scheduled-at')"
                                      :disabled="confirmationFirst"
                                      :hint="confirmationFirst ? $t('onboarding.commissioning.form.scheduled-after-confirmation') : $t('onboarding.commissioning.form.scheduled-hint-organization')"
                                      persistent-hint outlined dense />
                      </div>
                    </template>
                    <span>{{ $t('onboarding.commissioning.form.scheduled-after-confirmation') }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>

            <!-- Un véhicule : celui de la fiche, un des siens, ou celui que la personne déclarera. -->
            <template v-else-if="isVehicleKind">
              <div class="equipment-order__step mt-2"><span class="equipment-order__number">3</span>{{ $t('onboarding.commissioning.form.step-vehicle') }}</div>
              <v-alert v-if="vehicle" border="left" colored-border color="primary" elevation="1" dense class="mb-4">
                {{ vehicleLabel(vehicle) }}
              </v-alert>
              <vehicle-select
                  v-else-if="customerId"
                  v-model="vehicleId"
                  :customer-id="customerId"
                  :label="$t('onboarding.commissioning.form.vehicle')"
                  clearable
              />
              <p class="text-caption text--secondary">{{ $t(`onboarding.commissioning.form.vehicle-hint-${kind}`) }}</p>
            </template>

            <p v-else-if="kind === 'badge'" class="text-caption text--secondary">{{ $t('onboarding.commissioning.form.badge-hint') }}</p>

            <v-text-field
                v-model="externalReference"
                :label="$t('onboarding.commissioning.external-reference')"
                :hint="$t('onboarding.commissioning.form.external-reference-hint')" persistent-hint
                outlined dense class="mt-4"
            />
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <span v-if="deviceTypeMissing" class="text-caption error--text ml-2">{{ $t('onboarding.commissioning.form.device-type-required') }}</span>
          <span v-else class="text-caption text--secondary ml-2">{{ $t('onboarding.commissioning.form.next') }}</span>
          <v-spacer />
          <v-btn small text @click="$emit('input', false)">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" type="submit" :disabled="!kind || !whoKnown || deviceTypeMissing" :loading="working">{{ $t('onboarding.commissioning.form.create') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs'
import ChargingPointRepository from '@repository/ChargingPointRepository'
import ChargingPolicyRepository from '@repository/ChargingPolicyRepository'
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'
import LocationRepository from '@repository/LocationRepository'
import MemberRepository from '@repository/MemberRepository'
import MeterDeviceRepository from '@repository/MeterDeviceRepository'
import MemberSelect from '@blocks/Select/MemberSelect.vue'
import { classification, loadClassification } from '@includes/classification'
import VehicleSelect from '@blocks/Select/VehicleSelect.vue'
import CommissioningMixin, { INSTALLABLE_KINDS, ORGANIZATION_KINDS, VEHICLE_KINDS } from '../CommissioningMixin'
import NewLocationPicker from '../NewLocationPicker.vue'
import { designatedProfileId, deviceTypesByKind, equipmentKinds, kindsGivenBy, PROFILE_KINDS, PROFILES_BY_DEVICE_TYPE_KEY, PROFILES_KEY } from './policyCatalogue'

const DEFAULT_LOCATION_KEY = 'organization.default-location'
/** Ce qu'une adresse de pose doit dire au moins (comme le serveur). */
const ADDRESS_FIELDS = ['street', 'zipcode', 'city']

/**
 * Commander un équipement, en trois questions (spec section 12) : pour qui (l'organisation, un membre, une personne
 * pas encore membre), quel profil (par type d'équipement : il fixe l'usage et la licence), où (pour une personne : chez
 * elle — jamais un emplacement de l'organisation ; pour l'organisation : un de ses emplacements ou une nouvelle adresse).
 * Pour une personne, seul ce que sa politique de recharge désigne se commande ; le reste est dit, pas caché. Le serveur
 * reste juge : un refus s'affiche tel qu'il le dit.
 */
export default {
  name: 'EquipmentOrderDialog',

  components: { MemberSelect, NewLocationPicker, VehicleSelect },

  mixins: [CommissioningMixin],

  props: {
    value: { type: Boolean, default: false },
    organizationId: { type: String, required: true },
    /** member | organization : d'où l'on commande (la fiche d'un membre ou d'un véhicule, ou celle de l'organisation). */
    holder: { type: String, required: true, validator: v => ['member', 'organization'].includes(v) },
    /** Le membre, pour une commande de membre : la commande est pour lui. */
    member: { type: Object, default: null },
    /** Le véhicule, quand on vient de sa fiche : la commande le désigne. */
    vehicle: { type: Object, default: null },
    /** Les types proposés depuis ce point d'entrée (une borne depuis l'onglet Bornes…) ; vide : tous. */
    kinds: { type: Array, default: () => [] },
    /**
     * Les types du point d'entrée ne bornent que la commande pour l'organisation (l'onglet Bornes ou Compteurs) : pour un
     * membre ou une personne invitée, tout ce qui se commande pour elle — un véhicule compris —, comme depuis sa fiche.
     */
    kindsForOrganizationOnly: { type: Boolean, default: false },
    /** Depuis l'organisation, la réponse à « Pour qui » déjà donnée : organization | member | invitee. */
    presetWho: { type: String, default: null },
    /** Pour une personne pas encore membre : la politique dans laquelle l'inviter (sinon celle par défaut). */
    policyId: { type: String, default: null },
    /** Pour une personne pas encore membre déjà saisie ailleurs (l'invitation) : son nom, son e-mail, son téléphone. */
    presetPerson: { type: Object, default: null },
    /** Et ce que son invitation dit en plus : `overrides` (type de membre, groupes, équipements), `badges`. */
    invitation: { type: Object, default: null },
  },

  data() {
    return {
      kind: null,
      /** Depuis l'organisation : pour elle, un de ses membres, ou une personne pas encore membre (invitée). */
      target: 'organization',
      pickedMemberId: null,
      pickedMember: null,
      allKinds: [],
      notYetAvailable: [],
      policies: [],
      chosenPolicyId: null,
      policy: null,
      loadingPolicy: false,
      profiles: [],
      profilesLoaded: false,
      profileId: null,
      /** Les sous-types par type d'équipement (borne, compteur), et celui choisi pour chaque type. */
      deviceTypesMap: {},
      deviceTypeByKind: {},
      person: {},
      /** person | new | existing | place:<emplacement> */
      locationChoice: 'new',
      locationId: null,
      locations: [],
      /** Les lieux où le membre a déjà un équipement chez lui : { id, name, address }. */
      memberPlaces: [],
      newPlace: null,
      equipmentMode: 'unknown',
      prepared: null,
      equipments: [],
      equipmentSearch: null,
      searchingEquipments: false,
      desiredDate: null,
      scheduledAt: '',
      currentType: 'any',
      restrictions: { vendors: [], models: [] },
      vehicleId: null,
      externalReference: '',
      working: false,
    }
  },

  computed: {
    // Les constructeurs du classement (on peut encore taper une marque telle que la borne l'annonce).
    manufacturerNames() {
      return classification.manufacturers.filter(m => m.id).map(m => m.name)
    },
    // Les gammes, puis chaque type d'une gamme qui en a plusieurs ; des constructeurs choisis s'il y en a.
    modelChoices() {
      const chosen = this.restrictions.vendors.map(v => String(v).toLowerCase())
      const choices = []
      for (const manufacturer of classification.manufacturers) {
        if (chosen.length && !chosen.includes(manufacturer.name.toLowerCase())) {
          continue
        }
        for (const line of manufacturer.productLines) {
          if (line.id) {
            choices.push({id: line.id, text: `${manufacturer.name} · ${line.name}`})
          }
          if (line.types.length > 1 || !line.id) {
            for (const type of line.types) {
              choices.push({id: type.id, text: `${manufacturer.name} · ${line.name} · ${type.model || type.modelLabel || '?'}`})
            }
          }
        }
      }
      return choices
    },
    /** Pour qui, au bout du compte : un membre (désigné, choisi, ou invité) ou l'organisation. */
    who() {
      if (this.holder === 'member') return 'member'
      return this.target === 'organization' ? 'organization' : 'member'
    },

    invitee() {
      return this.holder === 'organization' && this.target === 'invitee'
    },

    /** Le membre pour qui l'on commande : celui de la fiche, ou celui choisi depuis l'organisation. */
    beneficiary() {
      if (this.holder === 'member') return this.member
      return this.target === 'member' ? this.pickedMember : null
    },

    /** La réponse à « Pour qui » suffit pour la suite : un membre choisi quand on commande pour un membre. */
    whoKnown() {
      return !(this.holder === 'organization' && this.target === 'member' && !this.pickedMember)
    },

    forWhom() {
      const name = this.memberLabel(this.member || {})
      return this.vehicle
        ? this.$t('onboarding.commissioning.form.for-member-vehicle', { name, vehicle: this.vehicleLabel(this.vehicle) })
        : this.$t('onboarding.commissioning.form.for-member', { name })
    },

    /** Ce que la politique donne à la personne, sinon le défaut du catalogue. */
    allowedByPolicy() {
      return this.policy ? kindsGivenBy(this.policy.settings, this.allKinds) : null
    },

    /**
     * Un véhicule pour un membre, depuis le back-office (sa fiche, celle du véhicule, ou l'organisation) : l'organisation
     * choisit le profil, la politique du conducteur ne bloque pas (le serveur le permet pour une commande du back-office).
     * Une personne pas encore membre suit la politique de son invitation.
     */
    organizationChoosesVehicle() {
      return this.who === 'member' && !this.invitee
    },

    /** L'organisation commande pour elle-même ce qui se pose chez elle : rien depuis un point d'entrée qui n'en propose pas. */
    organizationCanOrder() {
      return !this.kinds.length || this.kinds.some(kind => ORGANIZATION_KINDS.includes(kind))
    },

    /** Les types que ce point d'entrée propose, pour la réponse à « Pour qui » donnée. */
    offeredKinds() {
      const restricted = this.kinds.length && !(this.kindsForOrganizationOnly && this.who !== 'organization')
      return restricted ? this.allKinds.filter(k => this.kinds.includes(k)) : this.allKinds
    },

    /**
     * Les profils, par type d'équipement, que l'on peut commander ici ; et, pour un type qui ne se commande pas, pourquoi
     * en une phrase : la politique de la personne ne le permet pas, ou l'organisation n'a pas de profil de site.
     */
    profileGroups() {
      const offered = this.offeredKinds
        .filter(kind => !this.notYetAvailable.includes(kind))
        // L'organisation ne commande que ce qui se pose chez elle ; le reste n'a rien à faire dans sa liste.
        .filter(kind => this.who !== 'organization' || ORGANIZATION_KINDS.includes(kind))
      return offered.map(kind => {
        const vehicleChoice = this.organizationChoosesVehicle && VEHICLE_KINDS.includes(kind)
        if (this.who === 'member' && !vehicleChoice && this.allowedByPolicy && !this.allowedByPolicy.includes(kind)) {
          return { kind, deviceTypes: [], choices: [], reason: 'not-in-policy' }
        }
        if (!PROFILE_KINDS.includes(kind)) {
          return { kind, deviceTypes: [], choices: [{ key: kind, kind, profileId: null, title: this.kindLabel(kind), detail: this.$t(`onboarding.commissioning.form.kind-explained.${kind}`) }] }
        }
        const deviceTypes = this.offeredDeviceTypes(kind)
        const choices = this.fittingProfiles(kind).map(profile => ({
          key: profile.id,
          kind,
          profileId: profile.id,
          title: profile.name,
          usage: VEHICLE_KINDS.includes(kind) ? null : profile.usage,
          detail: this.licenceText(profile.licence) || this.$t(`onboarding.commissioning.form.kind-explained.${kind}`),
        }))
        let reason = null
        if (!choices.length && this.profilesLoaded) {
          if (deviceTypes.length && !this.deviceTypeByKind[kind]) reason = 'device-type-first'
          else reason = this.who === 'organization' ? 'no-site-profile' : (vehicleChoice ? 'no-vehicle-profile' : 'not-in-policy')
        }
        return { kind, deviceTypes, choices, reason }
      })
    },

    choices() {
      return this.profileGroups.flatMap(group => group.choices)
    },

    /** Une borne ou un compteur qui a des sous-types se commande avec le sien (le serveur le refuse sinon). */
    deviceTypeMissing() {
      return !!this.kind && INSTALLABLE_KINDS.includes(this.kind) && this.deviceTypesOf(this.kind).length > 0 && !this.deviceTypeByKind[this.kind]
    },

    /** Ce qui se pose (borne, compteur) : où, quoi, quand. */
    installable() {
      return INSTALLABLE_KINDS.includes(this.kind)
    },

    /** Chez une personne, la pose attend sa confirmation (prérequis « confirmation ») : pas de rendez-vous avant. */
    confirmationFirst() {
      return this.who === 'member' && this.installable
    },

    canManageProfiles() {
      return (this.currentUser?.permissions || []).includes('ONBOARDING_MODULE_MANAGE')
    },

    isVehicleKind() {
      return VEHICLE_KINDS.includes(this.kind)
    },

    customerId() {
      return this.beneficiary?.customer?.id || null
    },

    /** L'adresse de sa fiche (client, sinon membre), et si elle suffit pour poser. */
    personAddress() {
      return this.beneficiary?.customer?.address || this.beneficiary?.address || null
    },

    personAddressComplete() {
      return ADDRESS_FIELDS.every(field => String(this.personAddress?.[field] || '').trim() !== '')
    },

    /**
     * Où poser : pour l'organisation, un de ses emplacements ou une nouvelle adresse ; pour un membre, un lieu où il a déjà
     * un équipement, son adresse (complète), une nouvelle ; pour une personne pas encore membre, une nouvelle adresse.
     */
    placeOptions() {
      const t = key => this.$t(`onboarding.commissioning.form.${key}`)
      if (this.who === 'organization') {
        return [{ value: 'existing', label: t('location-existing') }, { value: 'new', label: t('location-new') }]
      }
      if (this.invitee) return [{ value: 'new', label: t('location-new') }]
      return [
        ...this.memberPlaces.map(place => ({
          value: `place:${place.id}`,
          label: this.$t('onboarding.commissioning.form.location-place', { name: place.name }),
          detail: this.formatAddress(place.address),
        })),
        {
          value: 'person',
          label: t('location-person'),
          detail: this.personAddressComplete ? this.formatAddress(this.personAddress) : t('location-person-incomplete'),
          disabled: !this.personAddressComplete,
        },
        { value: 'new', label: t('location-new') },
      ]
    },

    currentTypes() {
      return ['any', 'ac', 'dc'].map(value => ({ value, text: this.$t(`onboarding.commissioning.current-type.${value}`) }))
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.reset()
        this.load()
      }
    },

    pickedMemberId(memberId) {
      this.pickMember(memberId)
    },

    equipmentSearch(text) {
      if (text && text.length >= 2 && (!this.prepared || text !== this.prepared.label)) {
        this.searchEquipments(text)
      }
    },
  },

  methods: {
    loadClassification() {
      loadClassification().catch(err => this.notifyError(err))
    },

    memberLabel(member) {
      return [member.firstname, member.lastname].filter(Boolean).join(' ') || member.customer?.email || member.id || ''
    },

    vehicleLabel(vehicle) {
      return [vehicle.name, vehicle.registrationNumber].filter(Boolean).join(' · ') || vehicle.id
    },

    reset() {
      this.kind = null
      this.profileId = null
      this.deviceTypeByKind = {}
      this.target = this.holder === 'organization' && ['organization', 'member', 'invitee'].includes(this.presetWho) ? this.presetWho : 'organization'
      this.pickedMemberId = null
      this.pickedMember = null
      this.memberPlaces = []
      this.person = this.holder === 'member' ? { email: this.member?.customer?.email || null } : { ...(this.presetPerson || {}) }
      this.locationId = null
      this.newPlace = null
      this.equipmentMode = 'unknown'
      this.prepared = null
      this.equipments = []
      this.desiredDate = null
      this.scheduledAt = ''
      this.currentType = 'any'
      this.restrictions = { vendors: [], models: [] }
      this.vehicleId = null
      this.externalReference = ''
      this.$refs.form?.resetValidation()
    },

    required(value) {
      return !!(value && String(value).trim()) || this.$t('onboarding.commissioning.form.required')
    },

    async load() {
      this.loadingPolicy = true
      try {
        const [kinds, profiles, policies, deviceTypes] = await Promise.all([
          equipmentKinds().catch(() => null),
          EquipmentProfileRepository.list(this.organizationId).catch(() => null),
          ChargingPolicyRepository.list(this.organizationId).catch(() => []),
          deviceTypesByKind(),
          this.loadLocations(),
        ])
        this.deviceTypesMap = deviceTypes || {}
        this.profiles = profiles || []
        this.profilesLoaded = profiles !== null
        // Sans catalogue : les types de ce point d'entrée, sinon ceux que l'écran sait nommer.
        const named = Object.keys(this.$i18n.getLocaleMessage(this.$i18n.locale)?.onboarding?.commissioning?.kind || {})
        this.allKinds = kinds ? kinds.all : (this.kinds.length ? this.kinds : named)
        this.notYetAvailable = kinds ? kinds.notYetAvailable : []
        this.policies = policies
        this.chosenPolicyId = (policies.find(p => p.id === this.policyId) || policies.find(p => p.default) || policies[0] || {}).id || null
        if (this.holder === 'member') await this.loadMemberPlaces(this.member)
        await this.loadPolicy()
      } finally {
        this.loadingPolicy = false
      }
    },

    /**
     * La politique qui s'applique : pour une personne pas encore membre, celle choisie pour l'inviter ; sinon celle par
     * défaut de l'organisation (le serveur prend la même).
     */
    async loadPolicy() {
      const policyId = this.invitee ? this.chosenPolicyId : (this.policies.find(p => p.default) || {}).id || null
      this.policy = await CommissioningPolicyRepository.get(this.organizationId, null, policyId).catch(() => null)
      const defaultLocation = this.policy?.settings?.[DEFAULT_LOCATION_KEY]?.value
      if (this.who === 'organization' && defaultLocation && !this.locationId) this.locationId = defaultLocation
      this.resetPlace()
      this.preselect()
    },

    async choosePolicy(policyId) {
      this.chosenPolicyId = policyId
      this.loadingPolicy = true
      try {
        await this.loadPolicy()
      } finally {
        this.loadingPolicy = false
      }
    },

    /** Changer pour qui : la politique, les profils et les lieux possibles changent avec. */
    async whoChanged() {
      this.person = {}
      this.pickedMemberId = null
      this.pickedMember = null
      this.memberPlaces = []
      this.kind = null
      this.profileId = null
      this.deviceTypeByKind = {}
      await this.choosePolicy(this.chosenPolicyId)
    },

    async pickMember(memberId) {
      this.pickedMember = null
      this.memberPlaces = []
      if (!memberId) return
      try {
        const member = await MemberRepository.get(memberId)
        this.person = { email: member?.customer?.email || null }
        await this.loadMemberPlaces(member)
        this.pickedMember = member
      } catch (e) {
        this.notifyError(e)
      }
      this.resetPlace()
      this.preselect()
    },

    /** Les emplacements de ses bornes à domicile : là où il a déjà un équipement. */
    async loadMemberPlaces(member) {
      const points = await Promise.all((member?.homeChargingPoints || []).map(cp => ChargingPointRepository.get(cp.id).catch(() => null)))
      const places = {}
      for (const cp of points) {
        if (cp?.location?.id) places[cp.location.id] = { id: cp.location.id, name: cp.location.name || cp.name || cp.id, address: cp.location.address || null }
      }
      this.memberPlaces = Object.values(places)
    },

    /** Le premier lieu possible : l'emplacement par défaut de l'organisation, son adresse, sinon une nouvelle adresse. */
    resetPlace() {
      if (this.who === 'organization') {
        this.locationChoice = 'existing'
      } else if (this.invitee) {
        this.locationChoice = 'new'
      } else if (this.personAddressComplete) {
        this.locationChoice = 'person'
      } else {
        this.locationChoice = this.memberPlaces.length ? `place:${this.memberPlaces[0].id}` : 'new'
      }
    },

    /** Un seul choix possible : déjà fait (un seul sous-type proposé compris). */
    preselect() {
      for (const group of this.profileGroups) {
        if (group.deviceTypes.length === 1 && !this.deviceTypeByKind[group.kind]) this.$set(this.deviceTypeByKind, group.kind, group.deviceTypes[0])
      }
      const choices = this.choices
      if (this.kind && choices.some(this.isChosen)) return
      // Le profil de véhicule que sa politique désigne, quand il y en a plusieurs : le choix attendu, modifiable.
      const onlyVehicles = choices.length > 0 && choices.every(c => VEHICLE_KINDS.includes(c.kind))
      const designated = onlyVehicles
        ? choices.find(c => c.profileId === designatedProfileId(this.policy?.settings, c.kind, null))
        : null
      if (choices.length === 1) {
        this.choose(choices[0])
      } else if (designated) {
        this.choose(designated)
      } else {
        this.kind = null
        this.profileId = null
      }
    },

    isChosen(option) {
      return this.kind === option.kind && (this.profileId || null) === (option.profileId || null)
    },

    choose(option) {
      if (this.kind !== option.kind) {
        this.prepared = null
        this.equipments = []
      }
      this.kind = option.kind
      this.profileId = option.profileId || null
    },

    /**
     * Les profils possibles : pour une personne, celui que sa politique désigne, et lui seul (celui de son sous-type,
     * sinon celui de tous les sous-types) ; pour l'organisation, ses profils de site actifs qui visent ce sous-type ;
     * pour le véhicule d'un membre, les profils de ce service, celui que sa politique désigne en premier.
     */
    fittingProfiles(kind) {
      if (this.organizationChoosesVehicle && VEHICLE_KINDS.includes(kind)) {
        const designated = designatedProfileId(this.policy?.settings, kind, null)
        return this.profiles.filter(p => p.kind === kind && !p.archived)
          .sort((a, b) => (b.id === designated) - (a.id === designated))
      }
      const deviceType = this.deviceTypeByKind[kind] || null
      if (this.who === 'member') {
        const designated = designatedProfileId(this.policy?.settings, kind, deviceType)
        return this.profiles.filter(p => p.id === designated && !p.archived)
      }
      return this.profiles.filter(p => p.kind === kind && !p.archived && p.usage !== 'home' && this.coversDeviceType(p, deviceType))
    },

    deviceTypesOf(kind) {
      return INSTALLABLE_KINDS.includes(kind) ? (this.deviceTypesMap[kind] || []) : []
    },

    deviceTypeLabel(deviceType) {
      const key = `onboarding.device-type.${deviceType}`
      return this.$te(key) ? this.$t(key) : deviceType
    },

    /** Un profil vaut pour un sous-type s'il les vise tous (aucun nommé) ou s'il le nomme. */
    coversDeviceType(profile, deviceType) {
      const types = profile.deviceTypes || []
      return !deviceType || !types.length || types.includes(deviceType)
    },

    /**
     * Les sous-types proposés : pour une personne, tous si sa politique a un profil « tous », sinon ceux qui ont le
     * leur ; pour l'organisation, ceux qu'au moins un de ses profils de site vise.
     */
    offeredDeviceTypes(kind) {
      const all = this.deviceTypesOf(kind)
      if (!all.length) return []
      if (this.who === 'member') {
        const typed = this.policy?.settings?.[PROFILES_BY_DEVICE_TYPE_KEY]?.value?.[kind] || {}
        const forAll = this.profiles.find(p => p.id === this.policy?.settings?.[PROFILES_KEY]?.value?.[kind])
        if (!forAll) return all.filter(deviceType => !!typed[deviceType])
        // Le profil « tous » vaut pour ce qu'il vise : un profil limité à la borne ne propose pas le câble.
        return all.filter(deviceType => !!typed[deviceType] || this.coversDeviceType(forAll, deviceType))
      }
      const site = this.profiles.filter(p => p.kind === kind && !p.archived && p.usage !== 'home')
      return all.filter(deviceType => site.some(p => this.coversDeviceType(p, deviceType)))
    },

    /** Un autre sous-type : le profil choisi ne vaut peut-être plus ; un seul possible, il l'est d'office. */
    chooseDeviceType(kind, deviceType) {
      this.$set(this.deviceTypeByKind, kind, deviceType || null)
      if (this.kind === kind && !this.choices.some(this.isChosen)) {
        this.kind = null
        this.profileId = null
      }
      const group = this.profileGroups.find(g => g.kind === kind)
      if (deviceType && group && group.choices.length === 1) this.choose(group.choices[0])
    },

    /** « Abonnement · Formule » */
    licenceText(licence) {
      return [licence?.subscriptionName, licence?.formulaName].filter(Boolean).join(' · ')
    },

    /** Les emplacements de l'organisation : seulement pour une commande de l'organisation. */
    async loadLocations() {
      try {
        const result = await LocationRepository.search({ organizationId: this.organizationId, offset: 0, limit: 200, all: true }, 'equipment-order')
        this.locations = (result.items || []).map(l => ({ id: l.id, name: [l.name, l.address?.city].filter(Boolean).join(' · ') || l.id }))
      } catch (e) {
        this.locations = []
      }
    },

    /** Les équipements de l'organisation, avec ce qui les caractérise : on reconnaît le bon sans ouvrir sa fiche. */
    async searchEquipments(text) {
      this.searchingEquipments = true
      try {
        const search = { query: text, organizationId: this.organizationId, offset: 0, limit: 20 }
        if (this.kind === 'charging-point') {
          const result = await ChargingPointRepository.search(search)
          this.equipments = (result.items || []).map(cp => ({
            id: cp.id,
            label: cp.identifier === cp.name || !cp.name ? cp.identifier : `${cp.identifier} · ${cp.name}`,
            details: [
              [cp.chargingPointType?.manufacturerName || cp.chargingPointType?.vendorLabel, cp.chargingPointType?.productLineName || cp.chargingPointType?.modelLabel].filter(Boolean).join(' · '),
              cp.location?.name ? this.$t('onboarding.commissioning.form.equipment-at', { location: cp.location.name }) : this.$t('onboarding.commissioning.form.equipment-in-stock'),
            ].filter(Boolean).join(' · '),
          }))
        } else {
          const result = await MeterDeviceRepository.search(search)
          this.equipments = (result.items || []).map(md => ({
            id: md.id,
            label: md.name || md.id,
            details: [
              md.type ? this.$t(`onboarding.commissioning.form.meter-type.${md.type}`) : null,
              [md.brand, md.model].filter(Boolean).join(' '),
              md.location?.name ? this.$t('onboarding.commissioning.form.equipment-at', { location: md.location.name }) : this.$t('onboarding.commissioning.form.equipment-in-stock'),
            ].filter(Boolean).join(' · '),
          }))
        }
      } catch (e) {
        // Recherche interrompue par la frappe suivante : rien à signaler.
      } finally {
        this.searchingEquipments = false
      }
    },

    /** L'emplacement désigné : un des siens (organisation), ou un lieu où le membre a déjà un équipement. */
    chosenLocationId() {
      if (this.locationChoice === 'existing' && this.who === 'organization') return this.locationId
      if (this.locationChoice.startsWith('place:')) return this.locationChoice.slice('place:'.length)
      if (this.locationChoice === 'new') return this.newPlace?.locationId || null
      return null
    },

    /** Ce que la commande dit, selon ce qui se commande : l'emplacement et l'équipement d'une pose, le véhicule. */
    body() {
      const order = {
        organizationId: this.organizationId,
        kind: this.kind,
        holder: this.who,
        memberId: this.beneficiary?.id || null,
        externalReference: this.externalReference.trim() || null,
        profileId: PROFILE_KINDS.includes(this.kind) ? this.profileId : null,
        // La politique de son invitation, qui est aussi celle de la commande.
        policyId: this.invitee ? this.chosenPolicyId : null,
      }
      if (this.installable && this.deviceTypeByKind[this.kind]) order.deviceType = this.deviceTypeByKind[this.kind]
      if (this.invitee) order.person = this.person
      if (this.invitee && this.invitation) order.invitation = this.invitation
      if (this.installable) {
        const expected = this.equipmentMode === 'unknown'
          ? { vendors: this.restrictions.vendors, models: this.restrictions.models, currentType: this.currentType === 'any' ? null : this.currentType }
          : null
        // À son adresse : celle qu'on vient de lui montrer, écrite sur la commande (la fiche client peut ne pas l'avoir).
        const person = this.locationChoice === 'person' ? { ...this.person, postalAddress: this.personAddress } : this.person
        Object.assign(order, {
          person: this.who === 'member' ? person : {},
          locationId: this.chosenLocationId(),
          newLocation: this.locationChoice === 'new' ? this.newPlace?.newLocation || null : null,
          preparedEquipmentId: this.equipmentMode === 'existing' ? this.prepared?.id : null,
          restrictions: expected && (expected.vendors.length || expected.models.length || expected.currentType) ? expected : null,
          desiredDate: this.desiredDate ? dayjs(this.desiredDate).format() : null,
          scheduledAt: this.scheduledAt && !this.confirmationFirst ? dayjs(this.scheduledAt).format() : null,
        })
      } else if (this.isVehicleKind) {
        order.preparedEquipmentId = this.vehicle?.id || this.vehicleId || null
      }
      return order
    },

    async submit() {
      if (!this.kind || !this.whoKnown || !this.$refs.form.validate()) {
        return
      }
      this.working = true
      try {
        const created = await CommissioningRepository.create(this.body())
        this.notifySuccess('onboarding.commissioning.form.created')
        this.$emit('input', false)
        this.$emit('created', created)
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.working = false
      }
    },
  },
}
</script>

<style scoped>
.equipment-order__step {
  display: flex;
  align-items: center;
  font-weight: 700;
  margin-bottom: 8px;
}
.equipment-order__step--sub {
  font-weight: 500;
}
.equipment-order__number {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.equipment-order__choice--on {
  border: 2px solid var(--v-primary-base) !important;
  background: #fff6f4;
}
.equipment-order__unavailable {
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
